import { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { X, AlertCircle, LogOut, Users, Activity, TrendingUp, QrCode, Database, Mail, UserCircle, Stethoscope, Shield, User, Upload, Trash2, Camera } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarInset
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";

// Therapy approaches options (matching therapist dashboard)
const THERAPY_APPROACHES = [
  'CBT (Cognitive Behavioral Therapy)',
  'DBT (Dialectical Behavior Therapy)',
  'Psychodynamic Therapy',
  'Mindfulness-Based Therapy',
  'Solution-Focused Therapy',
  'Humanistic Therapy',
  'EMDR',
  'ACT (Acceptance and Commitment Therapy)'
];

// Session types options (matching therapist dashboard)
const SESSION_TYPES = [
  'Individual Session',
  'Couples Session',
  'Family Session',
  'Group Session',
  'Online Sessions',
  'In-Person Sessions'
];

// Languages options (matching therapist dashboard)
const LANGUAGES = [
  'English',
  'Spanish',
  'French',
  'German',
  'Mandarin',
  'Hindi',
  'Arabic',
  'Portuguese'
];

// Insurance providers (matching therapist dashboard)
const INSURANCE_PROVIDERS = [
  'Blue Cross Blue Shield',
  'Aetna',
  'UnitedHealthcare',
  'Cigna',
  'Humana',
  'Medicare',
  'Medicaid',
  'Out of Pocket'
];

// Specialization options
const SPECIALIZATIONS = [
  { value: 'anxiety', label: 'Anxiety & Stress Management' },
  { value: 'depression', label: 'Depression & Mood Disorders' },
  { value: 'trauma', label: 'Trauma & PTSD' },
  { value: 'relationship', label: 'Relationship & Family Therapy' },
  { value: 'addiction', label: 'Addiction & Substance Abuse' },
  { value: 'grief', label: 'Grief & Loss Counseling' },
  { value: 'eating', label: 'Eating Disorders' },
  { value: 'sleep', label: 'Sleep Disorders' },
  { value: 'career', label: 'Career Counseling' },
  { value: 'general', label: 'General Mental Health' }
];

// Type definitions for schedule and pricing
type DaySchedule = { enabled: boolean; slots: string[] };
type WeeklySchedule = {
  Monday: DaySchedule;
  Tuesday: DaySchedule;
  Wednesday: DaySchedule;
  Thursday: DaySchedule;
  Friday: DaySchedule;
  Saturday: DaySchedule;
  Sunday: DaySchedule;
};
type PricingInfo = { price: number; duration: number; currency: string };
type PricingData = {
  individual: PricingInfo;
  couples: PricingInfo;
  family: PricingInfo;
  group: PricingInfo;
};

export default function AddTherapist() {
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [therapistId, setTherapistId] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Check for edit mode via query parameter (admin console only)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('id');

    if (idParam) {
      const id = parseInt(idParam);
      if (!isNaN(id)) {
        setIsEditMode(true);
        setTherapistId(id);
        fetchTherapistData(id);
      }
    }
  }, []);

  const fetchTherapistData = async (id: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      let response = await fetch(`/api/therapists/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch therapist data');
      }

      let therapist = await response.json();

      // Handle redirect responses (if therapist not found or slug redirect)
      if (therapist.redirect) {
        // If it's a slug enforcement redirect (permanent), silently re-fetch with slug
        if (therapist.permanent && therapist.redirectTo) {
          const slugMatch = therapist.redirectTo.match(/\/therapist-profile\/([^/]+)$/);
          if (slugMatch) {
            const slug = slugMatch[1];
            console.log('Following redirect to slug:', slug);

            // Re-fetch using slug
            const slugResponse = await fetch(`/api/therapists/${slug}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (!slugResponse.ok) {
              throw new Error('Failed to fetch therapist data with slug');
            }

            const slugData = await slugResponse.json();

            // Check again for redirect (shouldn't happen, but safety check)
            if (slugData.redirect) {
              throw new Error(slugData.message || 'Therapist not found');
            }

            // Use the slug-fetched data instead
            console.log('Fetched therapist data via slug:', slugData);

            // Use slugData as the therapist data for consistent processing below
            therapist = slugData;
          }
        } else {
          // For other redirects (inactive therapist, not found), show error
          throw new Error(therapist.message || 'Therapist not found');
        }
      }

      console.log('Fetched therapist data:', therapist);

      // Populate form fields - Section 1: Account Information
      setEmail(therapist.email || '');
      setFirstName(therapist.name?.split(' ')[0] || '');
      setLastName(therapist.name?.split(' ').slice(1).join(' ') || '');
      setIsActive(therapist.isActive ?? true);
      setAvatar(therapist.avatar || '');
      if (therapist.avatar) {
        setAvatarPreview(therapist.avatar);
      }

      // Section 2: Professional Credentials
      setLicenseNumber(therapist.licenseNumber || '');
      setSpecialization(therapist.specialty || '');
      setProfessionalTitle(therapist.title || '');
      setYearsExperience(therapist.yearsExperience?.toString() || '');

      // Parse JSON fields - these come as arrays from the API response
      setSelectedApproaches(Array.isArray(therapist.approaches) ? therapist.approaches : []);
      
      // Handle sessionTypes - extract just the type names for UI, pricing is in pricing state
      if (Array.isArray(therapist.sessionTypes) && therapist.sessionTypes.length > 0) {
        const sessionTypeNames = therapist.sessionTypes.map((session: any) => {
          return typeof session === 'string' ? session : session.type;
        });
        setSelectedSessionTypes(sessionTypeNames);
      } else {
        setSelectedSessionTypes([]);
      }
      
      setSelectedLanguages(Array.isArray(therapist.languages) ? therapist.languages : []);

      // Section 3: Profile & Location
      setBio(therapist.bio || '');
      setLocation(therapist.location || '');
      setTimezone(therapist.timezone || 'UTC');

      // Section 4: Additional Details
      // Handle education - filter out empty strings
      const educationData = Array.isArray(therapist.education) ? therapist.education : [];
      setEducation(educationData.filter((e: any) => typeof e === 'string' && e.trim()).length > 0 ? educationData.filter((e: any) => typeof e === 'string' && e.trim()) : ['']);

      // Handle certifications - filter out empty strings
      const certificationsData = Array.isArray(therapist.certifications) ? therapist.certifications : [];
      setCertifications(certificationsData.filter((c: any) => typeof c === 'string' && c.trim()).length > 0 ? certificationsData.filter((c: any) => typeof c === 'string' && c.trim()) : ['']);

      // Handle insurance
      setSelectedInsurance(Array.isArray(therapist.insurance) ? therapist.insurance : []);

      // Section 5: Load weekly schedule
      if (therapist.availability) {
        try {
          const availabilityData = typeof therapist.availability === 'string' 
            ? JSON.parse(therapist.availability) 
            : therapist.availability;

          const transformedSchedule: WeeklySchedule = {
            Monday: { enabled: false, slots: [] },
            Tuesday: { enabled: false, slots: [] },
            Wednesday: { enabled: false, slots: [] },
            Thursday: { enabled: false, slots: [] },
            Friday: { enabled: false, slots: [] },
            Saturday: { enabled: false, slots: [] },
            Sunday: { enabled: false, slots: [] }
          };

          Object.keys(availabilityData).forEach((dayKey: string) => {
            const dayName = dayKey.charAt(0).toUpperCase() + dayKey.slice(1).toLowerCase() as keyof WeeklySchedule;

            if (transformedSchedule[dayName]) {
              const timeSlots = availabilityData[dayKey];

              if (Array.isArray(timeSlots) && timeSlots.length > 0) {
                // Direct slot loading - database stores individual slots like ["9:00 AM", "10:00 AM"]
                transformedSchedule[dayName] = {
                  enabled: true,
                  slots: timeSlots.filter((slot: string) => typeof slot === 'string' && slot.trim())
                };
              }
            }
          });

          console.log('✅ Transformed schedule (slot-based):', transformedSchedule);
          setWeeklySchedule(transformedSchedule);
        } catch (e) {
          console.error('❌ Error parsing availability data:', e);
        }
      }

      // Section 6: Load pricing data
      console.log('📝 Raw pricing data from API:', therapist.pricing, typeof therapist.pricing);
      console.log('📝 Full therapist object keys:', Object.keys(therapist));

      if (therapist.pricing) {
        try {
          const pricingData = typeof therapist.pricing === 'string' ? JSON.parse(therapist.pricing) : therapist.pricing;
          console.log('📝 Parsed pricing data:', pricingData);

          const newPricing = {
            individual: pricingData.individual || { price: 0, duration: 50, currency: 'INR' },
            couples: pricingData.couples || { price: 0, duration: 60, currency: 'INR' },
            family: pricingData.family || { price: 0, duration: 60, currency: 'INR' },
            group: pricingData.group || { price: 0, duration: 60, currency: 'INR' }
          };
          console.log('✅ Pricing data populated:', newPricing);
          console.log('📝 Setting pricing state with:', newPricing);
          setPricing(newPricing);

          // Verify state was set
          setTimeout(() => {
            console.log('📝 Pricing state after set:', pricing);
          }, 100);
        } catch (e) {
          console.error('❌ Error parsing pricing data:', e);
          console.error('❌ Error details:', e instanceof Error ? e.message : 'Unknown error');
        }
      } else {
        console.log('⚠️ No pricing data found in therapist response');
        console.log('📝 Pricing field value:', therapist.pricing);
        console.log('📝 Pricing field is null?', therapist.pricing === null);
        console.log('📝 Pricing field is undefined?', therapist.pricing === undefined);
      }

      setAcceptsInsurance(therapist.acceptsInsurance || false);
      setSlidingScale(therapist.slidingScale || false);
      setFreeConsultation(therapist.freeConsultation || false);

      // Set slug preview
      setSlugPreview(therapist.slug || '');

      console.log('Form populated successfully');

    } catch (error: any) {
      console.error('Failed to fetch therapist data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load therapist data',
        variant: 'destructive'
      });
      navigate('/admin?view=users-therapists');
    } finally {
      setLoading(false);
    }
  };

  // Section 1: Account Information
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [avatar, setAvatar] = useState("");
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  // Section 2: Professional Credentials (Required)
  const [licenseNumber, setLicenseNumber] = useState("");
  const [specialization, setSpecialization] = useState("");
  const [professionalTitle, setProfessionalTitle] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  const [selectedApproaches, setSelectedApproaches] = useState<string[]>([]);
  const [selectedSessionTypes, setSelectedSessionTypes] = useState<string[]>([]);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);

  // Section 3: Profile & Location (Optional)
  const [bio, setBio] = useState("");
  const [location, setLocation] = useState("");
  const [timezone, setTimezone] = useState("UTC");

  // Section 4: Additional Details (Optional)
  const [education, setEducation] = useState<string[]>([""]);
  const [certifications, setCertifications] = useState<string[]>([""]);
  const [selectedInsurance, setSelectedInsurance] = useState<string[]>([]);

  // Section 5: Weekly Schedule (New)
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>({
    Monday: { enabled: false, slots: [] },
    Tuesday: { enabled: false, slots: [] },
    Wednesday: { enabled: false, slots: [] },
    Thursday: { enabled: false, slots: [] },
    Friday: { enabled: false, slots: [] },
    Saturday: { enabled: false, slots: [] },
    Sunday: { enabled: false, slots: [] }
  });

  // Section 6: Pricing & Payment (New)
  const [pricing, setPricing] = useState<PricingData>({
    individual: { price: 0, duration: 50, currency: 'INR' },
    couples: { price: 0, duration: 60, currency: 'INR' },
    family: { price: 0, duration: 60, currency: 'INR' },
    group: { price: 0, duration: 60, currency: 'INR' }
  });

  // Additional fields for Section 4 / integration with other sections
  const [acceptsInsurance, setAcceptsInsurance] = useState(false);
  const [slidingScale, setSlidingScale] = useState(false);
  const [freeConsultation, setFreeConsultation] = useState(false);

  // Slug preview
  const [slugPreview, setSlugPreview] = useState("");

  // Error states
  const [accountErrors, setAccountErrors] = useState<{
    email?: string;
    password?: string;
    confirmPassword?: string;
    firstName?: string;
    lastName?: string;
  }>({});

  const [professionalErrors, setProfessionalErrors] = useState<{
    licenseNumber?: string;
    specialization?: string;
    approaches?: string;
    sessionTypes?: string;
    languages?: string;
  }>({});

  const [locationErrors, setLocationErrors] = useState<{ bio?: string }>({});

  // Pricing handler
  const handlePricingChange = (type: keyof PricingData, field: keyof PricingInfo, value: string | number) => {
    setPricing(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  // Update slug preview when name changes
  const updateSlugPreview = (first: string, last: string) => {
    if (first && last) {
      const slug = `${first.toLowerCase()}-${last.toLowerCase()}`.replace(/\s+/g, '-');
      setSlugPreview(slug);
    } else {
      setSlugPreview("");
    }
  };

  const handleFirstNameChange = (value: string) => {
    setFirstName(value);
    updateSlugPreview(value, lastName);
    if (accountErrors.firstName) {
      setAccountErrors({ ...accountErrors, firstName: undefined });
    }
  };

  const handleLastNameChange = (value: string) => {
    setLastName(value);
    updateSlugPreview(firstName, value);
    if (accountErrors.lastName) {
      setAccountErrors({ ...accountErrors, lastName: undefined });
    }
  };

  const handleAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: 'Invalid File Type',
        description: 'Only JPEG, PNG, and JPG files are allowed.',
        variant: 'destructive'
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Photo must be under 5MB.',
        variant: 'destructive'
      });
      return;
    }

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      setAvatarPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadAvatarFile = async (): Promise<string | null> => {
    if (!avatarFile) return avatar.trim() || null;

    setAvatarUploading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('avatar', avatarFile);

      const response = await fetch('/api/upload-avatar', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to upload photo');
      }

      const data = await response.json();
      return data.avatar;
    } catch (error: any) {
      toast({
        title: 'Upload Failed',
        description: error.message || 'Failed to upload photo. Please try again.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setAvatarUploading(false);
    }
  };

  const removeAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    setAvatar('');
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  };

  const toggleApproach = (approach: string) => {
    setSelectedApproaches(prev => 
      prev.includes(approach) 
        ? prev.filter(a => a !== approach)
        : [...prev, approach]
    );
    if (professionalErrors.approaches) {
      setProfessionalErrors({ ...professionalErrors, approaches: undefined });
    }
  };

  const toggleSessionType = (type: string) => {
    setSelectedSessionTypes(prev => 
      prev.includes(type) 
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
    if (professionalErrors.sessionTypes) {
      setProfessionalErrors({ ...professionalErrors, sessionTypes: undefined });
    }
  };

  const toggleLanguage = (lang: string) => {
    setSelectedLanguages(prev => 
      prev.includes(lang) 
        ? prev.filter(l => l !== lang)
        : [...prev, lang]
    );
    if (professionalErrors.languages) {
      setProfessionalErrors({ ...professionalErrors, languages: undefined });
    }
  };

  const toggleInsurance = (ins: string) => {
    setSelectedInsurance(prev => 
      prev.includes(ins) 
        ? prev.filter(i => i !== ins)
        : [...prev, ins]
    );
  };

  const addEducation = () => {
    setEducation([...education, ""]);
  };

  const updateEducation = (index: number, value: string) => {
    const updated = [...education];
    updated[index] = value;
    setEducation(updated);
  };

  const removeEducation = (index: number) => {
    setEducation(education.filter((_, i) => i !== index));
  };

  const addCertification = () => {
    setCertifications([...certifications, ""]);
  };

  const updateCertification = (index: number, value: string) => {
    const updated = [...certifications];
    updated[index] = value;
    setCertifications(updated);
  };

  const removeCertification = (index: number) => {
    setCertifications(certifications.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setAccountErrors({});
    setProfessionalErrors({});
    setLocationErrors({});

    // Comprehensive validation
    const accErrors: any = {};
    const profErrors: any = {};
    const locErrors: any = {};

    // Account validation
    if (!email.trim()) {
      accErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      accErrors.email = "Invalid email format";
    }

    // Password validation - required for create, optional for edit
    if (!isEditMode) {
      if (!password) {
        accErrors.password = "Password is required";
      } else if (password.length < 8) {
        accErrors.password = "Password must be at least 8 characters";
      }

      if (!confirmPassword) {
        accErrors.confirmPassword = "Please confirm password";
      } else if (password !== confirmPassword) {
        accErrors.confirmPassword = "Passwords do not match";
      }
    } else {
      // In edit mode, only validate if password is provided
      if (password && password.length < 8) {
        accErrors.password = "Password must be at least 8 characters";
      }

      if (password && password !== confirmPassword) {
        accErrors.confirmPassword = "Passwords do not match";
      }
    }

    if (!firstName.trim() || firstName.trim().length < 2) {
      accErrors.firstName = "First name must be at least 2 characters";
    }

    if (!lastName.trim() || lastName.trim().length < 2) {
      accErrors.lastName = "Last name must be at least 2 characters";
    }

    // Professional validation
    if (!licenseNumber.trim()) {
      profErrors.licenseNumber = "License number is required";
    }

    if (!specialization) {
      profErrors.specialization = "Specialization is required";
    }

    if (selectedApproaches.length === 0) {
      profErrors.approaches = "Please select at least one therapy approach";
    }

    if (selectedSessionTypes.length === 0) {
      profErrors.sessionTypes = "Please select at least one session type";
    }

    if (selectedLanguages.length === 0) {
      profErrors.languages = "Please select at least one language";
    }

    // Location validation
    if (bio && bio.length > 500) {
      locErrors.bio = "Bio must not exceed 500 characters";
    }

    // If there are errors, show them and don't proceed
    if (Object.keys(accErrors).length > 0) {
      setAccountErrors(accErrors);
      toast({
        title: "Account Information Errors",
        description: "Please correct the highlighted fields in Section 1",
        variant: "destructive"
      });
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    if (Object.keys(profErrors).length > 0) {
      setProfessionalErrors(profErrors);
      toast({
        title: "Professional Credentials Errors",
        description: "Please correct the highlighted fields in Section 2",
        variant: "destructive"
      });
      setTimeout(() => {
        const firstErrorElement = document.querySelector('[data-error="true"]');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    if (Object.keys(locErrors).length > 0) {
      setLocationErrors(locErrors);
      toast({
        title: "Profile Information Errors",
        description: "Please correct the highlighted fields in Section 3",
        variant: "destructive"
      });
      return;
    }

    // Transform sessionTypes to include pricing information
    const sessionTypesWithPricing = selectedSessionTypes.map(sessionType => {
      // Map session type to pricing key
      const pricingKey = sessionType.toLowerCase().replace(' therapy', '').replace(' ', '') as keyof PricingData;
      const sessionPricing = pricing[pricingKey];

      return {
        type: sessionType,
        price: sessionPricing?.price || 0,
        duration: sessionPricing?.duration || 50,
        currency: sessionPricing?.currency || 'INR'
      };
    });

    console.log('📝 Transformed sessionTypes with pricing:', sessionTypesWithPricing);

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      const uploadedAvatar = await uploadAvatarFile();

      if (isEditMode && therapistId) {
        // EDIT MODE - PATCH request, exclude email, licenseNumber, slug
        const therapistData: any = {
          // Section 1 - Only updatable fields
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          // Note: isActive is managed separately in User Management for security

          // Section 2 (Required) - Exclude licenseNumber (locked)
          specialization: specialization,
          title: professionalTitle.trim() || null,
          yearsExperience: yearsExperience ? parseInt(yearsExperience) : null,
          approaches: JSON.stringify(selectedApproaches),
          sessionTypes: JSON.stringify(sessionTypesWithPricing), // Updated with pricing
          languages: JSON.stringify(selectedLanguages),

          // Section 3 (Optional)
          avatar: uploadedAvatar,
          bio: bio.trim() || null,
          location: location.trim() || null,
          timezone,

          // Section 4 (Optional)
          education: JSON.stringify(education.filter(e => e.trim())),
          certifications: JSON.stringify(certifications.filter(c => c.trim())),
          insurance: JSON.stringify(selectedInsurance),

          // Slot-based availability (direct save)
          availability: JSON.stringify(
            Object.entries(weeklySchedule).reduce((acc, [day, config]) => {
              if (config.enabled && Array.isArray(config.slots) && config.slots.length > 0) {
                acc[day.toLowerCase()] = config.slots;
              }
              return acc;
            }, {} as Record<string, string[]>)
          ),
          pricing: JSON.stringify(pricing), // Keep for backward compatibility
          acceptsInsurance,
          slidingScale,
          freeConsultation,
        };

        // Only include password if it's being changed
        if (password && password.trim()) {
          therapistData.password = password;
        }

        const response = await fetch(`/api/therapists/${therapistId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(therapistData)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to update therapist');
        }

        toast({
          title: 'Success',
          description: `Therapist ${email} updated successfully`,
        });

        // Invalidate the users cache to force refetch in admin view
        await queryClient.invalidateQueries({ queryKey: ['/api/admin/users'] });

        // Also invalidate therapist-specific caches
        await queryClient.invalidateQueries({ queryKey: ['/api/therapists'] });

        // Force immediate refetch to ensure data is fresh
        await queryClient.refetchQueries({ queryKey: ['/api/admin/users'] });

        // Small delay to ensure cache invalidation completes
        await new Promise(resolve => setTimeout(resolve, 200));

        // Navigate to therapists management view
        navigate('/admin?view=users-therapists');

      } else {
        // CREATE MODE - POST request with all fields
        const therapistData = {
          // Section 1
          email,
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          isActive,

          // Section 2 (Required)
          licenseNumber: licenseNumber.trim(),
          specialization: specialization,
          title: professionalTitle.trim() || null,
          yearsExperience: yearsExperience ? parseInt(yearsExperience) : null,
          approaches: JSON.stringify(selectedApproaches),
          sessionTypes: JSON.stringify(sessionTypesWithPricing), // Updated with pricing
          languages: JSON.stringify(selectedLanguages),

          // Section 3 (Optional)
          avatar: uploadedAvatar,
          bio: bio.trim() || null,
          location: location.trim() || null,
          timezone,

          // Section 4 (Optional)
          education: JSON.stringify(education.filter(e => e.trim())),
          certifications: JSON.stringify(certifications.filter(c => c.trim())),
          insurance: JSON.stringify(selectedInsurance),

          // Slot-based availability (direct save)
          availability: JSON.stringify(
            Object.entries(weeklySchedule).reduce((acc, [day, config]) => {
              if (config.enabled && Array.isArray(config.slots) && config.slots.length > 0) {
                acc[day.toLowerCase()] = config.slots;
              }
              return acc;
            }, {} as Record<string, string[]>)
          ),
          pricing: JSON.stringify(pricing), // Keep for backward compatibility
          acceptsInsurance,
          slidingScale,
          freeConsultation,

          // Admin metadata
          verified: true,
          createdBy: user?.id
        };

        const response = await fetch('/api/auth/register/therapist', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(therapistData)
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create therapist');
        }

        toast({
          title: 'Success',
          description: `Therapist ${email} created successfully with slug: ${data.user?.slug || slugPreview}`,
        });

        // Navigate to therapists management view
        navigate('/admin?view=users-therapists');
      }

    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || (isEditMode ? 'Failed to update therapist' : 'Failed to create therapist'),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/admin-login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {loading && !therapistId ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading therapist data...</p>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">
              {isEditMode ? 'Edit Therapist' : 'Add New Therapist'}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditMode ? 'Update therapist account information' : 'Create a new therapist account'}
            </p>
            <p className="text-sm text-gray-500 mt-2">
              <span className="text-red-500">*</span> Fields marked with asterisk are required
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Public URL Preview - Always visible at top */}
            {slugPreview && (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="pt-6">
                  <div className="space-y-2">
                    <Label className="text-base font-semibold text-blue-900">
                      📌 Public Profile URL {isEditMode && <span className="text-xs font-normal">(Locked)</span>}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        value={`https://www.healthyowl.in/therapist-profile/${slugPreview}`}
                        readOnly
                        className="bg-white text-sm font-mono"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(`https://www.healthyowl.in/therapist-profile/${slugPreview}`);
                          toast({
                            title: "Copied!",
                            description: "Profile URL copied to clipboard",
                          });
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600">
                      {isEditMode 
                        ? "ℹ️ This URL is locked for SEO consistency and cannot be changed."
                        : "ℹ️ This URL will be generated automatically. A unique number will be added if the slug already exists."
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 1: Account Information */}
            <Card>
              <CardHeader>
                <CardTitle>Section 1: Account Information</CardTitle>
                <CardDescription>Basic account credentials (Required)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2" data-error={!!accountErrors.email}>
                  <Label className={accountErrors.email ? "text-red-600" : ""}>
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (accountErrors.email) {
                        setAccountErrors({ ...accountErrors, email: undefined });
                      }
                    }}
                    placeholder="therapist@example.com"
                    className={accountErrors.email ? "border-red-500 focus-visible:ring-red-500" : ""}
                    disabled={isEditMode}
                  />
                  {accountErrors.email && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {accountErrors.email}
                    </p>
                  )}
                  {isEditMode && (
                    <p className="text-xs text-gray-500">Email cannot be changed</p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!accountErrors.firstName}>
                    <Label className={accountErrors.firstName ? "text-red-600" : ""}>
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={firstName}
                      onChange={(e) => handleFirstNameChange(e.target.value)}
                      placeholder="Sarah"
                      className={accountErrors.firstName ? "border-red-500 focus-visible:ring-red-500" : ""}
                    />
                    {accountErrors.firstName && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {accountErrors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!accountErrors.lastName}>
                    <Label className={accountErrors.lastName ? "text-red-600" : ""}>
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      value={lastName}
                      onChange={(e) => handleLastNameChange(e.target.value)}
                      placeholder="Chen"
                      className={accountErrors.lastName ? "border-red-500 focus-visible:ring-red-500" : ""}
                    />
                    {accountErrors.lastName && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {accountErrors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2" data-error={!!accountErrors.password}>
                    <Label className={accountErrors.password ? "text-red-600" : ""}>
                      Password {!isEditMode && <span className="text-red-500">*</span>}
                      {isEditMode && <span className="text-gray-500 text-xs ml-1">(optional - leave empty to keep current)</span>}
                    </Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (accountErrors.password) {
                          setAccountErrors({ ...accountErrors, password: undefined });
                        }
                      }}
                      placeholder={isEditMode ? "Leave empty to keep current password" : "At least 8 characters"}
                      className={accountErrors.password ? "border-red-500 focus-visible:ring-red-500" : ""}
                    />
                    {accountErrors.password && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {accountErrors.password}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2" data-error={!!accountErrors.confirmPassword}>
                    <Label className={accountErrors.confirmPassword ? "text-red-600" : ""}>
                      Confirm Password {!isEditMode && <span className="text-red-500">*</span>}
                    </Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (accountErrors.confirmPassword) {
                          setAccountErrors({ ...accountErrors, confirmPassword: undefined });
                        }
                      }}
                      placeholder={isEditMode ? "Confirm new password if changing" : "Re-enter password"}
                      className={accountErrors.confirmPassword ? "border-red-500 focus-visible:ring-red-500" : ""}
                    />
                    {accountErrors.confirmPassword && (
                      <p className="text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {accountErrors.confirmPassword}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="text-base font-medium">Activate account immediately</label>
                    <p className="text-sm text-gray-500">Therapist can login and profile will be visible</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-4 h-4"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Professional Credentials */}
            <Card>
              <CardHeader>
                <CardTitle>Section 2: Professional Credentials</CardTitle>
                <CardDescription>Required professional information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2" data-error={!!professionalErrors.licenseNumber}>
                  <Label className={professionalErrors.licenseNumber ? "text-red-600" : ""}>
                    License Number <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={licenseNumber}
                    onChange={(e) => {
                      setLicenseNumber(e.target.value);
                      if (professionalErrors.licenseNumber) {
                        setProfessionalErrors({ ...professionalErrors, licenseNumber: undefined });
                      }
                    }}
                    placeholder="e.g., PSY12345"
                    className={professionalErrors.licenseNumber ? "border-red-500 focus-visible:ring-red-500" : ""}
                    disabled={isEditMode}
                  />
                  {professionalErrors.licenseNumber && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {professionalErrors.licenseNumber}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    {isEditMode 
                      ? "ℹ️ License number is locked and cannot be changed. Contact system admin for updates."
                      : "ℹ️ This will be locked after creation. Enter the therapist's professional license number."
                    }
                  </p>
                </div>

                <div className="space-y-2" data-error={!!professionalErrors.specialization}>
                  <Label className={professionalErrors.specialization ? "text-red-600" : ""}>
                    Specialization <span className="text-red-500">*</span>
                  </Label>
                  <Select value={specialization} onValueChange={(value) => {
                    setSpecialization(value);
                    if (professionalErrors.specialization) {
                      setProfessionalErrors({ ...professionalErrors, specialization: undefined });
                    }
                  }}>
                    <SelectTrigger className={professionalErrors.specialization ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select specialization" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPECIALIZATIONS.map(spec => (
                        <SelectItem key={spec.value} value={spec.value}>
                          {spec.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {professionalErrors.specialization && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {professionalErrors.specialization}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Professional Title</Label>
                  <Input
                    value={professionalTitle}
                    onChange={(e) => setProfessionalTitle(e.target.value)}
                    placeholder="e.g., Licensed Clinical Psychologist"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Years of Experience</Label>
                  <Input
                    type="number"
                    value={yearsExperience}
                    onChange={(e) => setYearsExperience(e.target.value)}
                    placeholder="e.g., 10"
                    min="0"
                  />
                </div>

                <div className="space-y-2" data-error={!!professionalErrors.approaches}>
                  <Label className={professionalErrors.approaches ? "text-red-600" : ""}>
                    Therapy Approaches <span className="text-red-500">*</span> (Select at least 1)
                  </Label>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                    professionalErrors.approaches ? "border-2 border-red-500" : "border border-gray-200"
                  }`}>
                    {THERAPY_APPROACHES.map(approach => (
                      <label 
                        key={approach} 
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <input 
                          type="checkbox" 
                          checked={selectedApproaches.includes(approach)}
                          onChange={() => toggleApproach(approach)}
                          className="w-4 h-4 rounded border-gray-300 text-sage-600 focus:ring-sage-500" 
                        />
                        <span className="text-sm text-gray-700">{approach}</span>
                      </label>
                    ))}
                  </div>
                  {professionalErrors.approaches && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {professionalErrors.approaches}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Selected: {selectedApproaches.length}</p>
                </div>

                <div className="space-y-2" data-error={!!professionalErrors.sessionTypes}>
                  <Label className={professionalErrors.sessionTypes ? "text-red-600" : ""}>
                    Session Types <span className="text-red-500">*</span> (Select at least 1)
                  </Label>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                    professionalErrors.sessionTypes ? "border-2 border-red-500" : "border border-gray-200"
                  }`}>
                    {SESSION_TYPES.map(type => (
                      <label 
                        key={type} 
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <input 
                          type="checkbox"
                          checked={selectedSessionTypes.includes(type)}
                          onChange={() => toggleSessionType(type)}
                          className="w-4 h-4 rounded border-gray-300 text-sage-600 focus:ring-sage-500" 
                        />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                  {professionalErrors.sessionTypes && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {professionalErrors.sessionTypes}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Selected: {selectedSessionTypes.length}</p>
                </div>

                <div className="space-y-2" data-error={!!professionalErrors.languages}>
                  <Label className={professionalErrors.languages ? "text-red-600" : ""}>
                    Languages <span className="text-red-500">*</span> (Select at least 1)
                  </Label>
                  <div className={`grid grid-cols-1 md:grid-cols-2 gap-3 p-3 rounded-md ${
                    professionalErrors.languages ? "border-2 border-red-500" : "border border-gray-200"
                  }`}>
                    {LANGUAGES.map(lang => (
                      <label 
                        key={lang} 
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <input 
                          type="checkbox"
                          checked={selectedLanguages.includes(lang)}
                          onChange={() => toggleLanguage(lang)}
                          className="w-4 h-4 rounded border-gray-300 text-sage-600 focus:ring-sage-500" 
                        />
                        <span className="text-sm text-gray-700">{lang}</span>
                      </label>
                    ))}
                  </div>
                  {professionalErrors.languages && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {professionalErrors.languages}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">Selected: {selectedLanguages.length}</p>
                </div>
              </CardContent>
            </Card>

            {/* Section 3: Profile & Location */}
            <Card>
              <CardHeader>
                <CardTitle>Section 3: Profile & Location</CardTitle>
                <CardDescription>Optional profile information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2" data-error={!!locationErrors.bio}>
                  <Label className={locationErrors.bio ? "text-red-600" : ""}>Professional Bio</Label>
                  <Textarea
                    value={bio}
                    onChange={(e) => {
                      setBio(e.target.value);
                      if (locationErrors.bio) {
                        setLocationErrors({ ...locationErrors, bio: undefined });
                      }
                    }}
                    placeholder="Brief professional description..."
                    rows={4}
                    maxLength={500}
                    className={locationErrors.bio ? "border-red-500 focus-visible:ring-red-500" : ""}
                  />
                  {locationErrors.bio && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {locationErrors.bio}
                    </p>
                  )}
                  <p className={`text-xs ${bio.length > 500 ? 'text-red-600' : 'text-gray-500'}`}>
                    {bio.length}/500 characters
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., New York, NY or Online"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="UTC">UTC (GMT+0:00)</SelectItem>
                      <SelectItem value="America/New_York">Eastern Time (GMT-5:00)</SelectItem>
                      <SelectItem value="America/Chicago">Central Time (GMT-6:00)</SelectItem>
                      <SelectItem value="America/Denver">Mountain Time (GMT-7:00)</SelectItem>
                      <SelectItem value="America/Los_Angeles">Pacific Time (GMT-8:00)</SelectItem>
                      <SelectItem value="Europe/London">London (GMT+0:00)</SelectItem>
                      <SelectItem value="Europe/Paris">Paris (GMT+1:00)</SelectItem>
                      <SelectItem value="Asia/Dubai">Dubai (GMT+4:00)</SelectItem>
                      <SelectItem value="Asia/Kolkata">India (GMT+5:30)</SelectItem>
                      <SelectItem value="Asia/Singapore">Singapore (GMT+8:00)</SelectItem>
                      <SelectItem value="Asia/Tokyo">Tokyo (GMT+9:00)</SelectItem>
                      <SelectItem value="Australia/Sydney">Sydney (GMT+11:00)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Section 4: Additional Details */}
            <Card>
              <CardHeader>
                <CardTitle>Section 4: Additional Details</CardTitle>
                <CardDescription>Optional credentials and insurance</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Education</Label>
                  {education.map((edu, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={edu}
                        onChange={(e) => updateEducation(index, e.target.value)}
                        placeholder="e.g., PhD in Clinical Psychology, Stanford University"
                      />
                      {education.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeEducation(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addEducation}>
                    Add Education
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Certifications</Label>
                  {certifications.map((cert, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={cert}
                        onChange={(e) => updateCertification(index, e.target.value)}
                        placeholder="e.g., Board Certified in Clinical Psychology"
                      />
                      {certifications.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeCertification(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" onClick={addCertification}>
                    Add Certification
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label>Insurance Accepted</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3 border border-gray-200 rounded-md">
                    {INSURANCE_PROVIDERS.map(ins => (
                      <label 
                        key={ins} 
                        className="flex items-center gap-2 cursor-pointer p-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        <input 
                          type="checkbox"
                          checked={selectedInsurance.includes(ins)}
                          onChange={() => toggleInsurance(ins)}
                          className="w-4 h-4 rounded border-gray-300 text-sage-600 focus:ring-sage-500" 
                        />
                        <span className="text-sm text-gray-700">{ins}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 5: Weekly Schedule */}
            <Card>
              <CardHeader>
                <CardTitle>Section 5: Weekly Schedule</CardTitle>
                <CardDescription>Select available time slots (1-hour slots from 9 AM to 8 PM)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quick-select buttons */}
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="w-full text-sm font-semibold mb-1">Quick Select:</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const availableSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM'];
                      Object.keys(weeklySchedule).forEach((day) => {
                        if (weeklySchedule[day as keyof WeeklySchedule].enabled) {
                          setWeeklySchedule(prev => ({
                            ...prev,
                            [day]: { ...prev[day as keyof WeeklySchedule], slots: availableSlots }
                          }));
                        }
                      });
                    }}
                  >
                    Morning (9-12)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const availableSlots = ['1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM'];
                      Object.keys(weeklySchedule).forEach((day) => {
                        if (weeklySchedule[day as keyof WeeklySchedule].enabled) {
                          setWeeklySchedule(prev => ({
                            ...prev,
                            [day]: { ...prev[day as keyof WeeklySchedule], slots: availableSlots }
                          }));
                        }
                      });
                    }}
                  >
                    Afternoon (1-5)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const availableSlots = ['6:00 PM', '7:00 PM', '8:00 PM'];
                      Object.keys(weeklySchedule).forEach((day) => {
                        if (weeklySchedule[day as keyof WeeklySchedule].enabled) {
                          setWeeklySchedule(prev => ({
                            ...prev,
                            [day]: { ...prev[day as keyof WeeklySchedule], slots: availableSlots }
                          }));
                        }
                      });
                    }}
                  >
                    Evening (6-8)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const monday = weeklySchedule.Monday;
                      if (!monday.enabled || !Array.isArray(monday.slots) || monday.slots.length === 0) {
                        toast({ title: "Set Monday first", description: "Configure Monday's slots to copy to other weekdays", variant: "destructive" });
                        return;
                      }
                      setWeeklySchedule(prev => ({
                        ...prev,
                        Tuesday: { enabled: true, slots: [...monday.slots] },
                        Wednesday: { enabled: true, slots: [...monday.slots] },
                        Thursday: { enabled: true, slots: [...monday.slots] },
                        Friday: { enabled: true, slots: [...monday.slots] }
                      }));
                    }}
                  >
                    Copy Monday → Weekdays
                  </Button>
                </div>

                {/* Day-by-day schedule */}
                {Object.keys(weeklySchedule).map((dayKey) => {
                  const day = dayKey as keyof WeeklySchedule;
                  const daySchedule = weeklySchedule[day];
                  const selectedSlots = Array.isArray(daySchedule.slots) ? daySchedule.slots : [];
                  const availableSlots = ['9:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM', '5:00 PM', '6:00 PM', '7:00 PM', '8:00 PM'];

                  return (
                    <div key={day} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`${day}-enabled`}
                            checked={daySchedule.enabled}
                            onChange={(e) => {
                              setWeeklySchedule(prev => ({
                                ...prev,
                                [day]: { ...prev[day], enabled: e.target.checked, slots: e.target.checked ? prev[day].slots : [] }
                              }));
                            }}
                            className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-teal-600"
                          />
                          <Label htmlFor={`${day}-enabled`} className="text-base font-semibold capitalize cursor-pointer">
                            {day}
                          </Label>
                          {daySchedule.enabled && selectedSlots.length > 0 && (
                            <Badge variant="secondary" className="ml-2">
                              {selectedSlots.length} slot(s)
                            </Badge>
                          )}
                        </div>
                        {daySchedule.enabled && selectedSlots.length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => {
                              setWeeklySchedule(prev => ({
                                ...prev,
                                [day]: { ...prev[day], slots: [] }
                              }));
                            }}
                          >
                            Clear All
                          </Button>
                        )}
                      </div>

                      {weeklySchedule[day].enabled && (
                        <div className="grid grid-cols-4 gap-2 ml-7">
                          {availableSlots.map((slot) => {
                            const isSelected = selectedSlots.includes(slot);
                            return (
                              <label
                                key={slot}
                                className={`flex items-center justify-center p-2 rounded-md border cursor-pointer transition-colors ${
                                  isSelected 
                                    ? 'bg-teal-100 border-teal-600 text-teal-900' 
                                    : 'bg-white border-gray-200 hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    setWeeklySchedule(prev => ({
                                      ...prev,
                                      [day]: {
                                        ...prev[day],
                                        slots: e.target.checked
                                          ? [...selectedSlots, slot]
                                          : selectedSlots.filter(s => s !== slot)
                                      }
                                    }));
                                  }}
                                  className="sr-only"
                                />
                                <span className="text-sm font-medium">{slot}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {!weeklySchedule[day].enabled && (
                        <p className="text-sm text-gray-500 ml-7">
                          Enable this day to select time slots
                        </p>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Section 6: Pricing & Payment */}
            <Card>
              <CardHeader>
                <CardTitle>Section 6: Pricing & Payment</CardTitle>
                <CardDescription>Set session prices and durations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {(['individual', 'couples', 'family', 'group'] as const).map((type) => (
                  <div key={type} className="p-4 border border-gray-200 rounded-md space-y-3">
                    <Label className="text-lg font-semibold capitalize">{type} Sessions</Label>
                    <div className="grid grid-cols-3 gap-4 items-center">
                      <div className="space-y-2">
                        <Label>Price</Label>
                        <Input
                          type="number"
                          value={pricing[type].price}
                          onChange={(e) => handlePricingChange(type, 'price', parseInt(e.target.value))}
                          placeholder="e.g., 100"
                          min="0"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Duration (mins)</Label>
                        <Input
                          type="number"
                          value={pricing[type].duration}
                          onChange={(e) => handlePricingChange(type, 'duration', parseInt(e.target.value))}
                          placeholder="e.g., 50"
                          min="1"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select 
                          value={pricing[type].currency} 
                          onValueChange={(value) => handlePricingChange(type, 'currency', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="INR">INR (₹)</SelectItem>
                            <SelectItem value="USD">USD ($)</SelectItem>
                            <SelectItem value="EUR">EUR (€)</SelectItem>
                            <SelectItem value="GBP">GBP (£)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="space-y-2">
                  <Label>Other Options</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={acceptsInsurance}
                        onChange={(e) => setAcceptsInsurance(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label>Accepts Insurance</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={slidingScale}
                        onChange={(e) => setSlidingScale(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label>Sliding Scale Available</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={freeConsultation}
                        onChange={(e) => setFreeConsultation(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <Label>Free Consultation</Label>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Section 7: Therapist Photo */}
            <Card>
              <CardHeader>
                <CardTitle>Section 7: Therapist Photo</CardTitle>
                <CardDescription>Upload a professional photo (optional). JPEG, PNG, JPG formats up to 5MB.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center gap-4">
                  {avatarPreview ? (
                    <div className="relative">
                      <img
                        src={avatarPreview}
                        alt="Therapist photo preview"
                        className="w-32 h-32 rounded-full object-cover border-2 border-gray-200"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 w-7 h-7 rounded-full"
                        onClick={removeAvatar}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="w-32 h-32 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400">
                      <Camera className="w-8 h-8 mb-1" />
                      <span className="text-xs">No photo</span>
                    </div>
                  )}

                  <div className="flex flex-col items-center gap-2">
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept=".jpeg,.jpg,.png,image/jpeg,image/png"
                      onChange={handleAvatarFileChange}
                      className="hidden"
                      id="avatar-upload"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => avatarInputRef.current?.click()}
                      className="gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {avatarPreview ? 'Change Photo' : 'Upload Photo'}
                    </Button>
                    <p className="text-xs text-gray-500 text-center">
                      Accepted formats: JPEG, PNG, JPG. Max size: 5MB.
                      {!avatarPreview && ' If no photo is uploaded, initials will be displayed.'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="submit"
                variant="default"
                className="flex-1"
                disabled={loading || avatarUploading}
              >
                {avatarUploading
                  ? "Uploading photo..."
                  : loading 
                    ? (isEditMode ? "Updating..." : "Creating...") 
                    : (isEditMode ? "Update Therapist Account" : "Create Therapist Account")
                }
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin?view=users-therapists')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}